import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Card, Col, InputNumber, Row, Select, Space, Typography } from 'antd';
import type { ShopTaxonomy } from '@shared/api/hooks/useTenantAdminConfig';

// ─── Default (tenant senza tassonomia configurata) ───────────────────────────

/** Valore di default quando il tenant non ha ancora una tassonomia shop. */
export const SHOP_TAXONOMY_DEFAULT: ShopTaxonomy = {
  macrocategories: [],
  subcategoriesByMacro: {},
  sizeSets: { senior: [], junior: [] },
  customizationPricing: {
    name: 5,
    numberByDigits: { '1': 2, '2': 4 },
    numberMax: 99,
  },
  inactiveMacrocategories: [],
};

/**
 * Normalizza un valore proveniente dal server (che può essere null o parziale)
 * in una tassonomia completa e sicura da renderizzare.
 */
export function normalizeShopTaxonomy(value?: ShopTaxonomy | null): ShopTaxonomy {
  if (!value) return { ...SHOP_TAXONOMY_DEFAULT };
  return {
    macrocategories: Array.isArray(value.macrocategories) ? value.macrocategories : [],
    subcategoriesByMacro:
      value.subcategoriesByMacro && typeof value.subcategoriesByMacro === 'object'
        ? value.subcategoriesByMacro
        : {},
    sizeSets: {
      senior: value.sizeSets?.senior ?? [],
      junior: value.sizeSets?.junior ?? [],
    },
    customizationPricing: {
      name: value.customizationPricing?.name ?? SHOP_TAXONOMY_DEFAULT.customizationPricing.name,
      numberByDigits: {
        '1':
          value.customizationPricing?.numberByDigits?.['1'] ??
          SHOP_TAXONOMY_DEFAULT.customizationPricing.numberByDigits['1'],
        '2':
          value.customizationPricing?.numberByDigits?.['2'] ??
          SHOP_TAXONOMY_DEFAULT.customizationPricing.numberByDigits['2'],
      },
      numberMax:
        value.customizationPricing?.numberMax ??
        SHOP_TAXONOMY_DEFAULT.customizationPricing.numberMax,
    },
    inactiveMacrocategories: Array.isArray(value.inactiveMacrocategories)
      ? value.inactiveMacrocategories.filter((m): m is string => typeof m === 'string')
      : [],
  };
}

// ─── Validazione ──────────────────────────────────────────────────────────────

/** Restituisce gli errori di validazione o un array vuoto se tutto è corretto. */
export function validateShopTaxonomy(t: ShopTaxonomy): string[] {
  const errors: string[] = [];
  const cp = t.customizationPricing;
  if (cp.name < 0) errors.push('Il prezzo del nome non può essere negativo.');
  if (cp.numberByDigits['1'] < 0 || cp.numberByDigits['2'] < 0) {
    errors.push('I prezzi del numero non possono essere negativi.');
  }
  if (cp.numberMax < 0) errors.push('Il numero massimo non può essere negativo.');
  // Le sotto-categorie orfane (macro non più presente) non bloccano il salvataggio,
  // ma le segnaliamo come warning informativo altrove.
  return errors;
}

// ─── Componente principale ────────────────────────────────────────────────────

export interface ShopTaxonomyTabProps {
  /**
   * Valore proveniente dal server. Se null/undefined il componente parte da
   * un default vuoto. Quando cambia (es. dopo un refetch) lo stato interno
   * viene resettato ai valori del server.
   */
  initialValue?: ShopTaxonomy | null;
  /** Chiamata ad ogni modifica con il nuovo valore completo. */
  onChange: (value: ShopTaxonomy) => void;
}

export function ShopTaxonomyTab({
  initialValue,
  onChange,
}: ShopTaxonomyTabProps): React.ReactElement {
  const [tax, setTax] = useState<ShopTaxonomy>(() => normalizeShopTaxonomy(initialValue));

  // Sincronizza lo stato interno quando arriva il valore dal server (post-fetch).
  // Non chiama onChange: il parent legge il draft dal proprio useEffect.
  useEffect(() => {
    setTax(normalizeShopTaxonomy(initialValue));
  }, [initialValue]);

  const update = useCallback(
    (next: ShopTaxonomy) => {
      setTax(next);
      onChange(next);
    },
    [onChange],
  );

  const setMacrocategories = useCallback(
    (macros: string[]) => {
      // Pulisce le sotto-categorie orfane quando una macro viene rimossa.
      const sub: Record<string, string[]> = {};
      for (const m of macros) {
        sub[m] = tax.subcategoriesByMacro[m] ?? [];
      }
      update({ ...tax, macrocategories: macros, subcategoriesByMacro: sub });
    },
    [tax, update],
  );

  const setSubcategories = useCallback(
    (macro: string, subs: string[]) => {
      update({
        ...tax,
        subcategoriesByMacro: { ...tax.subcategoriesByMacro, [macro]: subs },
      });
    },
    [tax, update],
  );

  const setSizeSet = useCallback(
    (band: 'senior' | 'junior', sizes: string[]) => {
      update({ ...tax, sizeSets: { ...tax.sizeSets, [band]: sizes } });
    },
    [tax, update],
  );

  const setPricing = useCallback(
    (patch: Partial<ShopTaxonomy['customizationPricing']>) => {
      update({
        ...tax,
        customizationPricing: { ...tax.customizationPricing, ...patch },
      });
    },
    [tax, update],
  );

  const validationErrors = validateShopTaxonomy(tax);

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={16}>
      <Alert
        type="info"
        showIcon
        message="Tassonomia catalogo shop"
        description="Definisci le macro-categorie, le sotto-categorie per ciascuna macro, i set di taglie (senior/junior) e i prezzi di personalizzazione. Questi valori popolano i menu a tendina nell'editor prodotti."
      />

      {validationErrors.length > 0 && (
        <Alert
          type="error"
          showIcon
          message="Configurazione non valida — il salvataggio è bloccato"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {validationErrors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          }
        />
      )}

      {/* ── Macro-categorie ────────────────────────────────────────────────── */}
      <Card size="small" title="Macro-categorie">
        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Digita e premi Invio per aggiungere una macro-categoria (es. Maglie, Felpe, Pantaloni).
        </Typography.Text>
        <Select
          mode="tags"
          style={{ width: '100%' }}
          placeholder="Aggiungi macro-categorie…"
          value={tax.macrocategories}
          onChange={setMacrocategories}
          tokenSeparators={[',']}
          open={false}
        />
      </Card>

      {/* ── Sotto-categorie per macro ──────────────────────────────────────── */}
      <Card size="small" title="Sotto-categorie per macro-categoria">
        {tax.macrocategories.length === 0 ? (
          <Typography.Text type="secondary">
            Aggiungi prima almeno una macro-categoria.
          </Typography.Text>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            {tax.macrocategories.map((macro) => (
              <Row key={macro} gutter={12} align="middle">
                <Col style={{ width: 160 }}>
                  <Typography.Text strong>{macro}</Typography.Text>
                </Col>
                <Col flex="auto">
                  <Select
                    mode="tags"
                    style={{ width: '100%' }}
                    placeholder={`Sotto-categorie di ${macro}…`}
                    value={tax.subcategoriesByMacro[macro] ?? []}
                    onChange={(v) => setSubcategories(macro, v)}
                    tokenSeparators={[',']}
                    open={false}
                  />
                </Col>
              </Row>
            ))}
          </Space>
        )}
      </Card>

      {/* ── Set taglie ─────────────────────────────────────────────────────── */}
      <Card size="small" title="Set di taglie">
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Typography.Text strong>Senior</Typography.Text>
            <Select
              mode="tags"
              style={{ width: '100%', marginTop: 6 }}
              placeholder="XS, S, M, L, XL, XXL…"
              value={tax.sizeSets.senior}
              onChange={(v) => setSizeSet('senior', v)}
              tokenSeparators={[',']}
              open={false}
            />
          </Col>
          <Col xs={24} md={12}>
            <Typography.Text strong>Junior</Typography.Text>
            <Select
              mode="tags"
              style={{ width: '100%', marginTop: 6 }}
              placeholder="5/6, 7/8, 9/10…"
              value={tax.sizeSets.junior}
              onChange={(v) => setSizeSet('junior', v)}
              tokenSeparators={[',']}
              open={false}
            />
          </Col>
        </Row>
      </Card>

      {/* ── Prezzi personalizzazione ───────────────────────────────────────── */}
      <Card size="small" title="Prezzi personalizzazione">
        <Row gutter={16}>
          <Col xs={24} sm={12} md={6}>
            <Typography.Text strong>Nome</Typography.Text>
            <InputNumber
              min={0}
              step={0.5}
              style={{ width: '100%', marginTop: 6 }}
              addonAfter="€"
              value={tax.customizationPricing.name}
              onChange={(v) => setPricing({ name: v ?? 0 })}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Typography.Text strong>Numero (1 cifra)</Typography.Text>
            <InputNumber
              min={0}
              step={0.5}
              style={{ width: '100%', marginTop: 6 }}
              addonAfter="€"
              value={tax.customizationPricing.numberByDigits['1']}
              onChange={(v) =>
                setPricing({
                  numberByDigits: { ...tax.customizationPricing.numberByDigits, '1': v ?? 0 },
                })
              }
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Typography.Text strong>Numero (2 cifre)</Typography.Text>
            <InputNumber
              min={0}
              step={0.5}
              style={{ width: '100%', marginTop: 6 }}
              addonAfter="€"
              value={tax.customizationPricing.numberByDigits['2']}
              onChange={(v) =>
                setPricing({
                  numberByDigits: { ...tax.customizationPricing.numberByDigits, '2': v ?? 0 },
                })
              }
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Typography.Text strong>Numero massimo</Typography.Text>
            <InputNumber
              min={0}
              precision={0}
              style={{ width: '100%', marginTop: 6 }}
              value={tax.customizationPricing.numberMax}
              onChange={(v) => setPricing({ numberMax: v ?? 0 })}
            />
          </Col>
        </Row>
      </Card>
    </Space>
  );
}
